import { useQuery } from '@tanstack/react-query';
import { createContext, useContext } from 'react';
import type { KbClient } from './client';
import type { SearchParams } from './types';

export const KbClientContext = createContext<KbClient | null>(null);

export function useKb(): KbClient {
  const client = useContext(KbClientContext);
  if (!client) throw new Error('useKb outside KbClientContext');
  return client;
}

export function useMe() {
  const kb = useKb();
  return useQuery({ queryKey: ['me'], queryFn: () => kb.me(), staleTime: 5 * 60_000 });
}

export function useSearch(tenant: string, params: SearchParams) {
  const kb = useKb();
  return useQuery({
    queryKey: ['search', tenant, params],
    queryFn: () => kb.searchItems(tenant, params),
    placeholderData: (previous) => previous,
  });
}

export function useTopics(tenant: string) {
  const kb = useKb();
  return useQuery({ queryKey: ['topics', tenant], queryFn: () => kb.topics(tenant) });
}

export function useTopic(tenant: string, id: string) {
  const kb = useKb();
  return useQuery({ queryKey: ['topic', tenant, id], queryFn: () => kb.topic(tenant, id) });
}

export function useItem(tenant: string, id: string) {
  const kb = useKb();
  return useQuery({ queryKey: ['item', tenant, id], queryFn: () => kb.item(tenant, id) });
}

export function useArtifact(tenant: string, id: string) {
  const kb = useKb();
  return useQuery({ queryKey: ['artifact', tenant, id], queryFn: () => kb.artifact(tenant, id) });
}
